import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  discount: number;
  discountType: 'percentage' | 'fixed';
  notes?: string;
}

interface POSCartStore {
  items: CartItem[];
  customerId?: string;
  customerName?: string;
  orderNotes?: string;
  vatEnabled: boolean;

  // Table context for linked orders
  linkedTableId?: string;
  linkedTableNumber?: string;
  linkedAreaId?: string;
  linkedOrderId?: string;

  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  loadItems: (items: CartItem[]) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  updateItemNote: (productId: string, note: string) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  setCustomer: (customerId: string, customerName: string) => void;
  clearCustomer: () => void;
  setOrderNotes: (notes: string) => void;
  setVatEnabled: (enabled: boolean) => void;

  // Table linking
  linkToTable: (tableId: string, tableNumber: string, areaId: string, orderId?: string) => void;
  clearTableLink: () => void;

  getSubtotal: () => number;
  getVATAmount: () => number;
  getFiscalStamp: () => number;
  getTotal: () => number;
  getItemTotal: (productId: string) => number;
}

export const usePOSCartStore = create<POSCartStore>()(
  persist(
    (set, get) => ({
      items: [],
      customerId: undefined,
      customerName: undefined,
      orderNotes: undefined,
      vatEnabled: true,
      linkedTableId: undefined,
      linkedTableNumber: undefined,
      linkedAreaId: undefined,
      linkedOrderId: undefined,

      addItem: (item) => {
        const existing = get().items.find((i) => i.productId === item.productId);
        
        if (existing) {
          set((state) => ({
            items: state.items.map((i) =>
              i.productId === item.productId
                ? { ...i, quantity: i.quantity + 1 }
                : i
            ),
          }));
        } else {
          set((state) => ({
            items: [...state.items, { ...item, quantity: 1 }],
          }));
        }
      },

      loadItems: (items) => {
        set({ items });
      },

      updateQuantity: (productId, quantity) => {
        if (quantity < 1) {
          get().removeItem(productId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.productId === productId ? { ...item, quantity } : item
          ),
        }));
      },

      updateItemNote: (productId, note) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.productId === productId ? { ...item, notes: note } : item
          ),
        }));
      },

      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((item) => item.productId !== productId),
        }));
      },

      clearCart: () => {
        set({ 
          items: [], 
          customerId: undefined, 
          customerName: undefined,
          orderNotes: undefined,
          linkedTableId: undefined,
          linkedTableNumber: undefined,
          linkedAreaId: undefined,
          linkedOrderId: undefined,
        });
      },

      setCustomer: (customerId, customerName) => {
        set({ customerId, customerName });
      },

      clearCustomer: () => {
        set({ customerId: undefined, customerName: undefined });
      },

      setOrderNotes: (notes) => {
        set({ orderNotes: notes });
      },

      setVatEnabled: (enabled) => {
        set({ vatEnabled: enabled });
      },

      linkToTable: (tableId, tableNumber, areaId, orderId) => {
        set({
          linkedTableId: tableId,
          linkedTableNumber: tableNumber,
          linkedAreaId: areaId,
          linkedOrderId: orderId,
        });
      },

      clearTableLink: () => {
        set({
          linkedTableId: undefined,
          linkedTableNumber: undefined,
          linkedAreaId: undefined,
          linkedOrderId: undefined,
        });
      },

      getSubtotal: () => {
        return get().items.reduce((sum, item) => {
          const itemTotal = item.quantity * item.unitPrice;
          const discount =
            item.discountType === 'percentage'
              ? (itemTotal * item.discount) / 100
              : item.discount;
          return sum + (itemTotal - discount);
        }, 0);
      },

      getVATAmount: () => {
        if (!get().vatEnabled) return 0;
        return get().items.reduce((sum, item) => {
          const itemTotal = item.quantity * item.unitPrice;
          const discount =
            item.discountType === 'percentage'
              ? (itemTotal * item.discount) / 100
              : item.discount;
          const netAmount = itemTotal - discount;
          return sum + (netAmount * item.vat) / 100;
        }, 0);
      },

      getFiscalStamp: () => {
        const subtotal = get().getSubtotal();
        return subtotal > 5000 ? 100 : 0;
      },

      getTotal: () => {
        const subtotal = get().getSubtotal();
        const vat = get().getVATAmount();
        const fiscalStamp = get().getFiscalStamp();
        return subtotal + vat + fiscalStamp;
      },

      getItemTotal: (productId) => {
        const item = get().items.find((i) => i.productId === productId);
        if (!item) return 0;

        const itemTotal = item.quantity * item.unitPrice;
        const discount =
          item.discountType === 'percentage'
            ? (itemTotal * item.discount) / 100
            : item.discount;
        return itemTotal - discount;
      },
    }),
    {
      name: 'pos-cart',
    }
  )
);
